import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useList, useData, useSession, post, orgID } from '@yingbaba/api';
import {
  ActionDialog,
  Button,
  ButtonLink,
  Empty,
  ErrorNotice,
  Image,
  Notice,
  PageTitle,
  Pagination,
  QueryState,
  Status,
  TagList,
  useToast,
} from '@yingbaba/ui';
import { ArrowRight, MessageSquare, Plus } from '@yingbaba/icons';
import { date, resourceCategories, cityOptions, siteURL } from '@yingbaba/utils';
import { CreateProjectButton, ProjectInvitations } from './ProjectTools';
import { MyTasks } from './MyTasks';
import type {
  Project,
  Notification,
  Resource,
  Demand,
  DemandCandidate,
  Conversation,
} from '@yingbaba/types';

export function ToolsDashboard() {
  const qc = useQueryClient(),
    toast = useToast();
  const projects = useList<Project>('/workspace/projects?page_size=6');
  const notices = useList<Notification>('/workspace/notifications?page_size=4');
  const session = useSession();
  const manage = ['owner', 'admin', 'manager'].includes(
    session.data?.organizations.find((o) => o.id === orgID())?.role || '',
  );
  return (
    <>
      <PageTitle
        title="创作协作工作台"
        description="把团队、任务、素材和推广放在同一个工作空间。"
        action={manage && <CreateProjectButton />}
      />
      <ProjectInvitations />
      <MyTasks compact />
      <section className="panel">
        <div className="panel-title">
          <h3>
            继续推进项目
            {projects.data ? (
              <span className="small muted"> · {projects.data.total} 个项目</span>
            ) : null}
          </h3>
          <ButtonLink to="/projects" variant="ghost">
            全部项目
            <ArrowRight size={15} />
          </ButtonLink>
        </div>
        <QueryState query={projects}>
          {!projects.data?.items.length && (
            <Empty
              title="从一个项目开始"
              description="整理创作目标，邀请团队，一起推进每个阶段。"
            />
          )}
          <div className="grid-2">
            {projects.data?.items.map((p) => (
              <Link key={p.id} to={`/projects/${p.id}`} className="project-tool-card">
                <strong>{p.title}</strong>
                <p>
                  {p.category || '创作项目'} · {date(p.deadline)}
                </p>
                <span className="tag">
                  {p.status === 'completed'
                    ? '已归档'
                    : p.status === 'cancelled'
                      ? '已关闭'
                      : '进行中'}
                </span>
              </Link>
            ))}
          </div>
        </QueryState>
      </section>
      <div className="grid-2 tool-shortcuts">
        <ButtonLink to="/digital-resources" variant="secondary">
          寻找数字素材
        </ButtonLink>
        <ButtonLink to="/demands" variant="secondary">
          发现合作需求
        </ButtonLink>
        <ButtonLink to="/resources" variant="secondary">
          管理影视资源
        </ButtonLink>
        <ButtonLink to="/promotion" variant="secondary">
          准备视频推广
        </ButtonLink>
      </div>
      <section className="panel">
        <h3>最近提醒</h3>
        <QueryState query={notices}>
          {!notices.data?.items.length && <p className="muted">暂时没有新提醒。</p>}
          {notices.data?.items.map((n) => (
            <Link
              className="list-item"
              to={n.href || '/notifications'}
              key={n.id}
              onClick={() => {
                if (!n.read)
                  void post(`/notifications/${n.id}/read`)
                    .then(() =>
                      qc.invalidateQueries({
                        predicate: (q) =>
                          /^\/workspace\/(notifications|overview)/.test(String(q.queryKey[0])),
                      }),
                    )
                    .catch(() => toast('未能更新已读状态，可在通知中心重试。'));
              }}
            >
              <div>
                <strong>{n.title}</strong>
                <p className="muted">{n.content}</p>
              </div>
            </Link>
          ))}
        </QueryState>
      </section>
    </>
  );
}

export function ToolsDemandWorkspace() {
  const { id } = useParams();
  const q = useData<Demand>(`/demands/${id}`);
  const candidates = useData<DemandCandidate[]>(`/demands/${id}/candidates`, !!q.data);
  const [error, setError] = useState<unknown>(null),
    [busy, setBusy] = useState('');
  const navigate = useNavigate();
  const own = q.data?.organization_id === orgID();
  const session = useSession();
  const manage = ['owner', 'admin', 'manager'].includes(
    session.data?.organizations.find((o) => o.id === orgID())?.role || '',
  );
  async function chat(providerOrganizationID: string) {
    setError(null);
    setBusy(providerOrganizationID);
    try {
      const c = await post<Conversation>(`/demands/${id}/conversation`, {
        provider_organization_id: providerOrganizationID,
      });
      navigate(`/messages?conversation=${c.id}`);
    } catch (e) {
      setError(e);
    } finally {
      setBusy('');
    }
  }
  return (
    <QueryState query={q}>
      {q.data && (
        <>
          <PageTitle
            title={q.data.title}
            description="交流合作意向，确认合适的团队后建立协作项目。"
            action={
              own &&
              manage && <CreateProjectButton title={q.data.title} category={q.data.category} />
            }
          />
          <div className="grid-2">
            <section className="panel">
              <TagList value={q.data.tags || q.data.category} />
              <h3>需求说明</h3>
              <p className="prose">{q.data.description}</p>
              <p className="muted">
                {q.data.city} · 计划 {date(q.data.deadline)}
              </p>
              {own && (
                <ButtonLink to={siteURL('www', `/publish?edit=${id}`)} variant="secondary">
                  编辑需求
                </ButtonLink>
              )}
              {!own && !candidates.data?.length && (
                <ActionDialog
                  title="表达合作意向"
                  trigger="报名参与"
                  submitLabel="提交合作意向"
                  path={`/demands/${id}/candidates/apply`}
                  fields={[
                    {
                      name: 'note',
                      label: '擅长方向与相关经验',
                      type: 'textarea',
                      min: 5,
                      max: 2000,
                    },
                  ]}
                  description="介绍团队擅长的方向和相关经验，方便对方了解你。"
                />
              )}
              {!own && !!candidates.data?.length && (
                <Button onClick={() => chat(orgID())}>
                  <MessageSquare size={16} />
                  联系需求方
                </Button>
              )}
            </section>
            <section className="panel">
              <h3>{own ? '感兴趣的团队' : '我的合作意向'}</h3>
              <ErrorNotice error={error} />
              <QueryState query={candidates} empty={!candidates.data?.length}>
                {candidates.data?.map((c) => (
                  <div className="list-item" key={c.id}>
                    <div className="grow">
                      <strong>{c.provider_name || '合作团队'}</strong>
                      <p>{c.note || '希望进一步了解需求'}</p>
                      {c.provider_id && (
                        <a className="link" href={siteURL('www', `/providers/${c.provider_id}`)}>
                          查看团队主页
                        </a>
                      )}
                    </div>
                    <Button
                      variant="secondary"
                      loading={busy === c.provider_organization_id}
                      onClick={() => chat(c.provider_organization_id)}
                    >
                      沟通
                    </Button>
                  </div>
                ))}
              </QueryState>
            </section>
          </div>
        </>
      )}
    </QueryState>
  );
}

export function ToolsResources() {
  const [page, setPage] = useState(1);
  const q = useList<Resource>(`/workspace/resources?page=${page}&page_size=18`);
  const session = useSession();
  const write = ['owner', 'admin', 'manager', 'member'].includes(
    session.data?.organizations.find((o) => o.id === orgID())?.role || '',
  );
  const keepExisting = (options: string[], value?: string) =>
    value && !options.includes(value) ? [...options, value] : options;
  const fields = (r?: Resource) => [
    { name: 'title', label: '资源名称', value: r?.title, min: 2, max: 100 },
    {
      name: 'category',
      label: '资源分类',
      value: r?.category === '网络素材' ? '' : r?.category,
      options: [
        { value: '', label: '请选择资源分类' },
        ...keepExisting(
          resourceCategories,
          r?.category === '网络素材' ? undefined : r?.category,
        ).map((value) => ({ value, label: value })),
      ],
    },
    {
      name: 'city',
      label: '所在城市',
      value: r?.city,
      options: keepExisting(cityOptions, r?.city),
    },
    {
      name: 'description',
      label: '资源介绍',
      type: 'textarea',
      value: r?.description,
      min: 10,
      max: 8000,
    },
    { name: 'cover', label: '封面图片地址（可选）', value: r?.cover, required: false },
  ];
  return (
    <>
      <PageTitle
        title="我的影视资源"
        description="展示场地、人员与器材，让合适的合作方找到你。"
        action={
          write && (
            <ActionDialog
              title="发布影视资源"
              protectDraft="discard-only"
              trigger={
                <>
                  <Plus size={16} />
                  发布资源
                </>
              }
              submitLabel="提交资源审核"
              path="/resources"
              fields={fields()}
              transform={(v) => ({ ...v, capacity: 1, price_minor: 0, unit: '' })}
            />
          )
        }
      />
      <Notice>
        视频、图片、音乐或模板等可下载素材，请前往{' '}
        <Link to="/digital-resources?tab=supplier">数字素材供稿</Link>。
      </Notice>
      <QueryState query={q}>
        {!q.data?.items.length && (
          <Empty
            title="展示你的影视资源"
            description="发布场地、人员或器材，介绍适合的使用场景，让合作方更容易找到你。"
          />
        )}
        <div className="grid-3">
          {q.data?.items.map((r) => (
            <article className="panel" key={r.id}>
              <Image src={r.cover} alt={r.title} className="supply-cover" />
              <div className="row spread">
                <h3>{r.title}</h3>
                <Status value={r.status} />
              </div>
              <p className="muted small">
                {r.city} · {r.category}
              </p>
              <p className="prose small">{r.description}</p>
              {r.category === '网络素材' && (
                <Notice>
                  这条素材尚未上架。请补齐文件及授权资料后通过数字素材供稿；如提供拍摄或制作服务，可编辑为相应的影视资源分类。
                </Notice>
              )}
              {r.review_reason && <Notice>{r.review_reason}</Notice>}
              <div className="row wrap">
                {write && (
                  <ActionDialog
                    title="编辑影视资源"
                    protectDraft="discard-only"
                    trigger="编辑资料"
                    method="PUT"
                    path={`/resources/${r.id}`}
                    submitLabel="保存并提交审核"
                    fields={fields(r)}
                    transform={(v) => ({
                      ...v,
                      capacity: r.capacity || 1,
                      price_minor: r.price_minor || 0,
                      unit: r.unit || '',
                    })}
                  />
                )}
                <ButtonLink to={siteURL('www', `/resources/${r.id}`)} variant="secondary">
                  查看资源
                </ButtonLink>
                <ButtonLink to="/messages" variant="ghost">
                  处理咨询
                </ButtonLink>
              </div>
            </article>
          ))}
        </div>
      </QueryState>
      <Pagination page={page} total={q.data?.total || 0} size={18} onPage={setPage} />
    </>
  );
}
